import {
  completeCheckout,
  confirmBnplOrderLoan,
  createBill,
  createBillPayLoan,
  createBnplOrderLoan,
  createDirectLoan,
  getBillPaymentOptions,
  getContractPreview,
  getLoanOptions,
  getOrderCustomer,
  getOrderPaymentOptions,
  getPublicRetailDisplayPaymentOptions,
  getUnsignedLoanContract1,
  notifyOrderVoid,
  prepareBnplPaymentIntent,
} from "@/generated/api/sdk.gen"
import type {
  CompleteCheckoutRequest,
  ConfirmBnplLoanRequest,
  CreateBillPayLoanRequest,
  CreateBillRequest,
  CreateBnplOrderLoanRequest,
  CreateDirectLoanRequest,
  GetBillPaymentOptionsData,
  PrepareBnplPaymentIntentRequest,
  RetailDisplayPaymentOptionsRequest,
} from "@/generated/api/types.gen"
import type { Api } from "@/platform/api/api"
import { body, conversionHeaders } from "@/platform/api/gateways/common"

type BillPaymentOptionsQuery = NonNullable<GetBillPaymentOptionsData["query"]>

export const checkoutGateway = (api: Api) => ({
  contractPreview: (repaymentOptionUuid: string) => body(getContractPreview({ ...api.request({ cache: "no-store" }), query: { repaymentOptionUuid } })),

  loanContractPreview: (loanUuid: string) => body(getUnsignedLoanContract1({ ...api.request({ cache: "no-store" }), path: { loanUuid } })),

  complete: (request: CompleteCheckoutRequest, idempotencyKey: string, etag: string, conversionId?: string) =>
    body(
      completeCheckout({
        ...api.request(),
        headers: { ...conversionHeaders(conversionId), "Idempotency-Key": idempotencyKey, "If-Match": etag },
        body: request,
      })
    ),

  // A signed-in shopper sends the session so api-v2 quotes their own secured offer; a guest stays public.
  displayPaymentOptions: (request: RetailDisplayPaymentOptionsRequest, signedIn = false) =>
    body(getPublicRetailDisplayPaymentOptions({ ...api.request({ credentials: signedIn ? "cookie-session" : "public" }), body: request })),

  bnplOrderCustomer: (checkoutToken: string) => body(getOrderCustomer({ ...api.request(), path: { checkoutToken } })),

  bnplPaymentOptions: (checkoutToken: string, signal?: AbortSignal) =>
    body(getOrderPaymentOptions({ ...api.request({ credentials: "checkout-token", signal }), path: { checkoutToken } })),

  // Not in the generated SDK until the snapshot is regenerated from the deployed API; the response is read through bnplPartnerOf.
  bnplPartner: (slug: string, signal?: AbortSignal): Promise<unknown> =>
    body(api.client.get({ ...api.request({ credentials: "public", signal }), url: `/order/partner/${encodeURIComponent(slug)}` })),

  createBnplOrderLoan: (request: CreateBnplOrderLoanRequest) => body(createBnplOrderLoan({ ...api.request(), body: request })),

  confirmBnplOrderLoan: (request: ConfirmBnplLoanRequest) => body(confirmBnplOrderLoan({ ...api.request(), body: request })),

  prepareBnplDownPayment: (request: PrepareBnplPaymentIntentRequest) => body(prepareBnplPaymentIntent({ ...api.request(), body: request })),

  notifyOrderVoid: async (checkoutToken: string, loanUuid?: string, repaymentOptionUuid?: string): Promise<void> => {
    await notifyOrderVoid({ ...api.request({ credentials: "checkout-token" }), body: { checkoutToken, loanUuid, repaymentOptionUuid } })
  },

  createBill: (request: CreateBillRequest) => body(createBill({ ...api.request(), body: request })),

  billPaymentOptions: (billUuid: string, query: BillPaymentOptionsQuery) => body(getBillPaymentOptions({ ...api.request(), path: { uuid: billUuid }, query })),

  createBillPayLoan: (request: CreateBillPayLoanRequest) => body(createBillPayLoan({ ...api.request(), body: request })),

  directLoanOptions: () => body(getLoanOptions(api.request())),

  createDirectLoan: (request: CreateDirectLoanRequest) => body(createDirectLoan({ ...api.request(), body: request })),
})

export type CheckoutGateway = ReturnType<typeof checkoutGateway>
